/* eslint-disable */
import React, { useState } from "react";
import AppRoutes from "core/routing/AppRoutes";
import ErrorBoundary from "core/error-handling/ErrorBoundary";
import useAuth from "hooks/useAuth";
import { MenuProvider } from "contexts/MenuContext";
import { CommonProvider } from "contexts/CommonContext";
import useCheckIsTablet from "hooks/useCheckIsTablet";
import Notification from "firebaseConfig/Notifications";
import { useUserInfoStore } from "@/stores/userInfoStore";
import { routes } from "constants/routes";
import "./App.scss";
import useIOSFCMToken from "hooks/useIOSFCMToken";
import { pushToDataLayer, isStandalone } from "core/utils/utils.helper";
import { isAppleDevice } from "helpers/userAgent";
import {
  pushAppsFlyerCuid,
  pushAppsFlyerGtmEvent,
} from "core/utils/appsflyer-gtm.helper";
import asyncComponentLoader from "core/loader";
import { setMarketingConsentCookieSignout } from "./helpers/marketingConsent.helper";
import { APPSFLYER_EVENTS } from "constants/appsflyer";
// Lazy load PWA Badge component
const PWABadge = asyncComponentLoader(() =>
  import("components/PWABadge").then((module) => ({
    default: module.PWABadge,
  })),
);

const App: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileRef, setMobileRef] = useState("");
  const isTablet = useCheckIsTablet(); //hook for checking if the screen size is for tablet or not.
  const auth: any = useAuth(); //hook for accessing auth values
  const isIOSApp = isAppleDevice() && isStandalone();
  const loggedInUserData = useUserInfoStore((s) => s.userInfo); //loggedin user state
  React.useEffect(() => {
    if (auth?.isAuthenticated) {
      pushToDataLayer(
        {
          event: "userIdentification",
          user: {
            accountId: loggedInUserData?.accountId,
          },
        },
        false,
      );
      // Set AppsFlyer CUID (web dataLayer + native Android)
      if (loggedInUserData?.accountId) {
        pushAppsFlyerCuid(loggedInUserData.accountId);
      }
    }
  }, [auth?.isAuthenticated, loggedInUserData?.accountId]);

  const sessionStartedTracked = React.useRef<boolean>(false);

  // Track app session started once
  React.useEffect(() => {
    if (!sessionStartedTracked.current) {
      pushAppsFlyerGtmEvent(APPSFLYER_EVENTS.APP_SESSION_STARTED, {
        timestamp: Date.now(),
      });
      sessionStartedTracked.current = true;
    }
  }, []);
  //function for handling signout
  const handleSignOut = () => {
    //To set marketing consent cookie on sign out with expiry
    setMarketingConsentCookieSignout();
    window.location.href = routes.logout.path;
  };
  //hook for requesting IOS notification
  useIOSFCMToken();

  return (
    <ErrorBoundary>
      <CommonProvider
        value={{ isTablet, handleSignOut, mobileRef, setMobileRef }}
      >
        <MenuProvider value={{ collapsed, setCollapsed }}>
          <PWABadge />
          <AppRoutes />
          {isIOSApp && <Notification />}
        </MenuProvider>
      </CommonProvider>
    </ErrorBoundary>
  );
};

export default App;
